/**
 * The revert menu (spec 5.3): reverts at four granularities — milestone / slot /
 * selection / whole-schedule. Milestone and slot options appear when exactly one point
 * is selected; the selection option appears for any selection; the whole-schedule
 * "Reset all dates" always appears and first shows a confirm stating the count of
 * edited dates it would discard ("Discard 37 edited dates?").
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, OverflowMenu, OverflowMenuItem } from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import { MILESTONE_LABELS } from './constants';
import { selectionToPoints, type SelectionSet } from './selection-model';
import type { RevertScope } from './types';
import styles from './revert-menu.module.scss';

export interface RevertMenuProps {
  selection: SelectionSet;
  editedDateCount: number;
  onRevert: (scope: RevertScope) => void;
}

/**
 * Renders the revert overflow menu + the whole-schedule reset confirm.
 *
 * @param {RevertMenuProps} props - The selection, the edited-date count, and the revert handler.
 * @return {JSX.Element} The menu.
 */
export function RevertMenu({
  selection,
  editedDateCount,
  onRevert,
}: RevertMenuProps) {
  const [confirmReset, setConfirmReset] = useState(false);
  const points = selectionToPoints(selection);
  const single = points.length === 1 ? points[0] : null;

  return (
    <div className={styles.wrapper}>
      <OverflowMenu
        aria-label="Revert dates"
        flipped
        size="sm"
        iconDescription="Revert dates"
      >
        {single && (
          <OverflowMenuItem
            itemText={`Revert ${MILESTONE_LABELS[single.field]} date`}
            onClick={() =>
              onRevert({
                kind: 'milestone',
                slotId: single.slotId,
                field: single.field,
              })
            }
          />
        )}
        {single && (
          <OverflowMenuItem
            itemText="Revert this well's dates"
            onClick={() => onRevert({ kind: 'slot', slotId: single.slotId })}
          />
        )}
        {points.length > 0 && (
          <OverflowMenuItem
            itemText="Revert selected dates"
            onClick={() => onRevert({ kind: 'selection', points })}
          />
        )}
        <OverflowMenuItem
          hasDivider
          isDelete
          itemText="Reset all dates…"
          disabled={editedDateCount === 0}
          onClick={() => setConfirmReset(true)}
        />
      </OverflowMenu>

      {/* Portal the confirm to <body>: rendered in place it lives under the parent
          schedule-dialog Modal, whose container Carbon gives a `transform` while open.
          A transformed ancestor makes this confirm's `position: fixed` resolve to that
          container (offset ~right) instead of the viewport, so it opened off-center.
          Portaling to <body> escapes that containing block → it centers on the viewport,
          i.e. the tear-sheet center (same pattern as DestroyConfirmation). */}
      {confirmReset &&
        createPortal(
          <Modal
            open
            danger
            size="sm"
            modalHeading="Reset all dates"
            primaryButtonText="Discard edits"
            secondaryButtonText="Cancel"
            onRequestClose={() => setConfirmReset(false)}
            onRequestSubmit={() => {
              onRevert({ kind: 'all' });
              setConfirmReset(false);
            }}
          >
            <p>
              Discard <FormattedCount value={editedDateCount} /> edited date
              {editedDateCount === 1 ? '' : 's'}? Dates return to their modeled
              or reported values.
            </p>
          </Modal>,
          document.body
        )}
    </div>
  );
}
